<template>
  <b-card class="averages-card">
    <b-row>
      <b-col>
        <b-row align-h="between">
          <b-col cols="auto">
            <b-row>
              <b-col cols="auto">
                <b-avatar variant="primary" size="40" class="shadow mb-2" v-b-tooltip.hover.top="description">
                  <i class="flaticon-questions-circular-button text-white cursor-pointer icon-2x" />
                </b-avatar>
              </b-col>
              <b-col align-self="center" cols="auto">
                <h1>Average payments</h1>
              </b-col>
            </b-row>
          </b-col>
          <b-col cols="auto">
            <b-dropdown right text="Filters" variant="primary" boundary="window">
              <b-dropdown-form>
                <b-form-checkbox-group v-model="selected">
                  <template v-if="availableFilters.length > 0">
                    <b-form-checkbox
                      v-for="filter in availableFilters"
                      :key="filter.title"
                      :value="filter.title"
                    >
                      {{ filter.title }}
                    </b-form-checkbox>
                  </template>
                  <template v-else>
                    <p class="text-muted">No hay filtros disponibles</p>
                  </template>
                </b-form-checkbox-group>
              </b-dropdown-form>
            </b-dropdown>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-row v-if="byTypeRows.length" class="mb-3">
      <b-col cols="12">
        <div class="meta-panel">
          <h6 class="meta-title mb-2">Average by visit type</h6>
          <div class="table-responsive">
            <table class="table table-sm table-borderless mb-0 by-type-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th class="text-right">Count</th>
                  <th class="text-right">Total</th>
                  <th class="text-right">Average</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in byTypeRows" :key="row.type_visits_id || row.prefix">
                  <td>
                    <span class="font-weight-semibold">{{ row.prefix }}</span>
                    <span v-if="row.name" class="text-muted small ml-1">{{ row.name }}</span>
                  </td>
                  <td class="text-right">{{ row.count }}</td>
                  <td class="text-right">${{ formatMoney(row.total) }}</td>
                  <td class="text-right">${{ formatMoney(row.average) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </b-col>
    </b-row>

    <div v-if="series.length" class="avg-visual">
      <div class="avg-kpis">
        <div class="avg-kpi">
          <span>Selected</span>
          <strong>{{ getCategoryName(activeSeriesName) }}</strong>
        </div>
        <div class="avg-kpi">
          <span>Period average</span>
          <strong>${{ formatMoney(selectedPeriodAvg) }}</strong>
        </div>
        <div class="avg-kpi">
          <span>Highest day</span>
          <strong>${{ formatMoney(selectedPeak.value) }}</strong>
          <small v-if="selectedPeak.label">{{ selectedPeak.label }}</small>
        </div>
        <div class="avg-kpi">
          <span>Days with data</span>
          <strong>{{ selectedPoints.length }}</strong>
        </div>
      </div>

      <div class="avg-measures" role="tablist" aria-label="Average measure">
        <button
          v-for="item in series"
          :key="item.name"
          type="button"
          class="avg-measure"
          :class="{ 'is-active': activeSeriesName === item.name }"
          @click="selectSeries(item.name)"
        >
          {{ getCategoryName(item.name) }}
        </button>
      </div>

      <b-row class="mb-3">
        <b-col cols="12" class="mb-3">
          <h6 class="avg-section-title">Daily comparison</h6>
          <p class="avg-hint">
            Heatmap of daily averages by category. Darker / warmer = higher average. Click a row to focus that category below.
          </p>
          <div class="avg-compare-chart">
            <vue-apex-charts
              type="heatmap"
              :height="compareChartHeight"
              :options="compareChartOptions"
              :series="compareSeries"
            />
          </div>
        </b-col>
        <b-col cols="12">
          <h6 class="avg-section-title">Daily trend · {{ getCategoryName(activeSeriesName) }}</h6>
          <p class="avg-hint">Detail for the selected category. Click a day to focus it. Zoom with the toolbar.</p>
          <div class="avg-daily-chart">
            <vue-apex-charts
              type="bar"
              height="320"
              :options="dailyChartOptions"
              :series="dailySeries"
            />
          </div>
          <button
            v-if="selectedDay"
            type="button"
            class="avg-selection"
            @click="clearSelectedDay"
          >
            {{ selectedDay.label }} · ${{ formatMoney(selectedDay.value) }}
            <span>Clear</span>
          </button>
        </b-col>
      </b-row>
    </div>

    <b-row v-if="avgs.length > 0">
      <b-col
        v-for="({ label, value }, key) in avgs"
        :key="key"
        cols="12"
        sm="6"
        md="4"
        lg="4"
        class="mb-3"
      >
        <div
          class="radial-chart-container"
          :class="{ 'is-active': activeSeriesName === label }"
          @click="selectSeries(label)"
        >
          <div class="chart-wrapper">
            <vue-apex-charts
              :options="radialBarOptions(label, value)"
              :series="[percentage(value)]"
              type="radialBar"
              height="200"
            />
          </div>
          <div class="chart-label">
            <h6 class="text-center mb-0">{{ getCategoryName(label) }}</h6>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import {
  BFormCheckboxGroup,
  BDropdownForm,
  BFormCheckbox,
  BCard,
  BAvatar,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import { readDailyAverageValue } from '@/core/utils/dailyAverageFields'

const SERIES_COLORS = {
  TOTAL: '#0D47A1',
  AVG_TOTAL: '#0D47A1',
  NP: '#1B5E20',
  AVG_NP: '#1B5E20',
  NC: '#00695C',
  AVG_NC: '#00695C',
  FU: '#FF6F00',
  AVG_FU: '#FF6F00',
  PI: '#D50000',
  AVG_PI: '#D50000',
  PP: '#AA00FF',
  AVG_PP: '#AA00FF',
  RS: '#0091EA',
  AVG_RS: '#0091EA',
  CV: '#FF3D00',
  AVG_CV: '#FF3D00',
}

const SERIES_COLORS_MUTED = {
  TOTAL: '#90CAF9',
  AVG_TOTAL: '#90CAF9',
  NP: '#A5D6A7',
  AVG_NP: '#A5D6A7',
  NC: '#80CBC4',
  AVG_NC: '#80CBC4',
  FU: '#FFE082',
  AVG_FU: '#FFE082',
  PI: '#EF9A9A',
  AVG_PI: '#EF9A9A',
  PP: '#E1BEE7',
  AVG_PP: '#E1BEE7',
  RS: '#81D4FA',
  AVG_RS: '#81D4FA',
  CV: '#FFCC80',
  AVG_CV: '#FFCC80',
}

export default {
  components: {
    BFormCheckboxGroup,
    VueApexCharts,
    BCard,
    BAvatar,
    BDropdownForm,
    BFormCheckbox,
  },
  props: {
    averages: {
      type: Array,
      default: () => [],
      required: true,
    },
    dates: {
      type: Object,
      default: () => {},
      required: true,
    },
    byType: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      avgs: [],
      series: [],
      selected: [],
      activeSeriesName: '',
      selectedDayTs: null,
      description: 'Averages diarios del periodo de la fecha selecionada',
    }
  },
  computed: {
    byTypeRows() {
      if (!Array.isArray(this.byType)) return []
      return this.byType.filter(
        row => (parseInt(row.count, 10) || 0) > 0 || (parseFloat(row.total) || 0) > 0
      )
    },
    availableFilters() {
      const filters = []
      if (Array.isArray(this.averages)) {
        this.averages.forEach(({ title, label }) => {
          filters.push({ title, label })
        })
      }
      return filters
    },
    activeSeries() {
      return this.series.find(item => item.name === this.activeSeriesName) || this.series[0] || null
    },
    selectedPoints() {
      const data = (this.activeSeries && this.activeSeries.data) || []
      return data.filter(point => point && point.y != null && Number.isFinite(Number(point.y)))
    },
    selectedPeriodAvg() {
      if (!this.selectedPoints.length) return 0
      const sum = this.selectedPoints.reduce((acc, point) => acc + Number(point.y), 0)
      return sum / this.selectedPoints.length
    },
    selectedPeak() {
      if (!this.selectedPoints.length) return { value: 0, label: '' }
      const peak = this.selectedPoints.reduce((best, point) =>
        Number(point.y) > Number(best.y) ? point : best
      )
      return {
        value: peak.y,
        label: this.formatDay(peak.x),
      }
    },
    selectedDay() {
      if (this.selectedDayTs == null) return null
      const point = this.selectedPoints.find(item => Number(item.x) === Number(this.selectedDayTs))
      if (!point) return null
      return {
        label: this.formatDay(point.x),
        value: point.y,
      }
    },
    dailySeries() {
      return [
        {
          name: this.getCategoryName(this.activeSeriesName),
          data: this.selectedPoints.map(point => ({
            x: point.x,
            y: Number(point.y),
          })),
        },
      ]
    },
    dailyChartOptions() {
      const points = this.selectedPoints
      const selectedTs = this.selectedDayTs
      const color = SERIES_COLORS[this.activeSeriesName] || '#0D47A1'
      const muted = SERIES_COLORS_MUTED[this.activeSeriesName] || '#90CAF9'
      return {
        chart: {
          type: 'bar',
          toolbar: { show: true },
          animations: { enabled: true, easing: 'easeinout', speed: 450 },
          events: {
            dataPointSelection: (event, chartContext, config) => {
              const point = points[config.dataPointIndex]
              if (!point) return
              this.selectedDayTs =
                Number(this.selectedDayTs) === Number(point.x) ? null : Number(point.x)
            },
          },
        },
        plotOptions: {
          bar: {
            borderRadius: 5,
            columnWidth: '52%',
            distributed: true,
          },
        },
        colors: points.map(point => {
          if (selectedTs == null) return color
          return Number(point.x) === Number(selectedTs) ? color : muted
        }),
        dataLabels: { enabled: false },
        legend: { show: false },
        grid: {
          borderColor: '#cfd8dc',
          strokeDashArray: 3,
          padding: { bottom: 8 },
        },
        xaxis: {
          type: 'datetime',
          tickAmount: Math.min(6, Math.max(points.length, 1)),
          labels: {
            datetimeUTC: false,
            format: 'dd',
            rotate: 0,
            hideOverlappingLabels: true,
            showDuplicates: false,
            style: { colors: '#263238', fontSize: '11px', fontWeight: 700 },
          },
          axisBorder: { color: '#90CAF9' },
          axisTicks: { color: '#90CAF9' },
        },
        yaxis: {
          labels: {
            formatter: value => `$${Math.round(value || 0)}`,
            style: { colors: '#263238', fontSize: '11px', fontWeight: 700 },
          },
        },
        tooltip: {
          x: { format: 'dd MMM yyyy' },
          y: {
            formatter: value => `$${this.formatMoney(value)}`,
          },
        },
      }
    },
    compareChartHeight() {
      const rows = Math.max(this.series.length, 3)
      return Math.min(460, 90 + rows * 52)
    },
    compareSeries() {
      return this.series.map(item => ({
        name: this.getShortCategoryName(item.name),
        data: (item.data || [])
          .filter(point => point && point.y != null && Number.isFinite(Number(point.y)))
          .map(point => ({
            x: this.formatDayShort(point.x),
            y: Math.round(Number(point.y)),
          })),
      }))
    },
    compareChartOptions() {
      return {
        chart: {
          type: 'heatmap',
          toolbar: { show: true },
          animations: { enabled: true, easing: 'easeinout', speed: 400 },
          events: {
            dataPointSelection: (event, chartContext, { seriesIndex }) => {
              const item = this.series[seriesIndex]
              if (item) this.selectSeries(item.name)
            },
          },
        },
        plotOptions: {
          heatmap: {
            shadeIntensity: 0.55,
            radius: 4,
            useFillColorAsStroke: false,
            colorScale: {
              ranges: [
                { from: 0, to: 40, name: 'Low', color: '#1565C0' },
                { from: 41, to: 90, name: 'Mid', color: '#0D47A1' },
                { from: 91, to: 140, name: 'High', color: '#E65100' },
                { from: 141, to: 100000, name: 'Peak', color: '#BF360C' },
              ],
            },
          },
        },
        dataLabels: {
          enabled: true,
          style: {
            colors: ['#FFFFFF'],
            fontSize: '11px',
            fontWeight: 900,
          },
          formatter: value => (value == null || Number(value) === 0 ? '' : String(Math.round(value))),
          dropShadow: {
            enabled: true,
            top: 0,
            left: 0,
            blur: 2,
            color: '#000000',
            opacity: 0.85,
          },
        },
        legend: {
          show: true,
          position: 'top',
          horizontalAlign: 'left',
          fontSize: '11px',
          fontWeight: 700,
        },
        stroke: {
          width: 1,
          colors: ['#ffffff'],
        },
        grid: {
          padding: { left: 4, right: 8, top: 0, bottom: 0 },
        },
        xaxis: {
          labels: {
            rotate: -45,
            rotateAlways: true,
            style: { colors: '#263238', fontSize: '10px', fontWeight: 700 },
          },
          tooltip: { enabled: false },
        },
        yaxis: {
          labels: {
            style: { colors: '#0D47A1', fontSize: '12px', fontWeight: 800 },
          },
        },
        tooltip: {
          y: {
            formatter: value => `$${this.formatMoney(value)}`,
          },
        },
      }
    },
  },
  watch: {
    averages() {
      this.filterData()
    },
    selected() {
      this.filterData(true)
    },
  },
  mounted() {
    this.filterData()
  },
  methods: {
    selectSeries(name) {
      if (!name) return
      this.activeSeriesName = name
      this.selectedDayTs = null
    },
    clearSelectedDay() {
      this.selectedDayTs = null
    },
    formatDay(timestamp) {
      const date = new Date(Number(timestamp))
      if (!Number.isFinite(date.getTime())) return ''
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    },
    formatDayShort(timestamp) {
      const date = new Date(Number(timestamp))
      if (!Number.isFinite(date.getTime())) return ''
      const day = String(date.getDate()).padStart(2, '0')
      const month = date.toLocaleDateString('en-US', { month: 'short' })
      return `${day} ${month}`
    },
    getCategoryName(label) {
      const categoryNames = {
        CV: 'Covid 19 Vacunación',
        FU: 'Follow Up',
        NC: 'New Consultation',
        NP: 'New patient',
        PI: 'Pending Injection',
        PP: 'Pending procedure',
        RS: 'Result',
        TOTAL: 'Total',
        AVG_CV: 'Covid 19 Vacunación',
        AVG_FU: 'Follow Up',
        AVG_NC: 'New Consultation',
        AVG_NP: 'New patient',
        AVG_PI: 'Pending Injection',
        AVG_PP: 'Pending procedure',
        AVG_RS: 'Result',
        AVG_TOTAL: 'Total',
      }
      return categoryNames[label] || label
    },
    getShortCategoryName(label) {
      const shortNames = {
        CV: 'CV',
        FU: 'FU',
        NC: 'NC',
        NP: 'NP',
        PI: 'PI',
        PP: 'PP',
        RS: 'RS',
        TOTAL: 'Total',
        AVG_CV: 'CV',
        AVG_FU: 'FU',
        AVG_NC: 'NC',
        AVG_NP: 'NP',
        AVG_PI: 'PI',
        AVG_PP: 'PP',
        AVG_RS: 'RS',
        AVG_TOTAL: 'Total',
      }
      return shortNames[label] || label
    },
    radialBarOptions(label, value) {
      let color
      const numValue = parseFloat(value) || 0

      if (numValue >= 0 && numValue < 100) {
        color = '#FF6B6B'
      } else if (numValue >= 100 && numValue < 250) {
        color = '#FFD93D'
      } else if (numValue >= 250) {
        color = '#6BCF7F'
      } else {
        color = '#E0E0E0'
      }
      return {
        chart: {
          height: 200,
          type: 'radialBar',
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          radialBar: {
            startAngle: -135,
            endAngle: 225,
            hollow: {
              margin: 0,
              size: '60%',
              background: '#fff',
              imageOffsetX: 0,
              imageOffsetY: 0,
              position: 'front',
              dropShadow: {
                enabled: true,
                top: 2,
                left: 0,
                blur: 3,
                opacity: 0.2,
              },
            },
            track: {
              background: '#f1f1f1',
              strokeWidth: '67%',
              margin: 0,
              dropShadow: {
                enabled: false,
              },
            },
            dataLabels: {
              show: true,
              name: {
                show: true,
                color: '#666',
                fontSize: '11px',
                fontWeight: 600,
              },
              value: {
                formatter: function () {
                  return value
                },
                color: '#333',
                fontSize: '16px',
                fontWeight: 'bold',
                show: true,
              },
            },
          },
        },
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'dark',
            type: 'horizontal',
            shadeIntensity: 0.5,
            gradientToColors: [color],
            inverseColors: true,
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 100],
            colorStops: [
              {
                offset: 0,
                color: color,
                opacity: 1,
              },
              {
                offset: 100,
                color: color,
                opacity: 1,
              },
            ],
          },
        },
        stroke: {
          lineCap: 'round',
        },
        labels: [label],
      }
    },
    percentage(valor) {
      const numValor = parseFloat(valor) || 0
      if (numValor < 0) return 0
      if (numValor > 1000) return 100
      return (numValor / 1000) * 100
    },
    filterData(selected = false) {
      if (!this.averages || !Array.isArray(this.averages) || this.averages.length === 0) {
        this.series = []
        this.avgs = []
        this.activeSeriesName = ''
        this.selectedDayTs = null
        return
      }

      const series = []
      this.avgs = []

      this.averages.forEach(({ title, averages, label }) => {
        if (selected && this.selected.length && !this.selected.includes(title)) return
        if (!averages || !Array.isArray(averages)) return

        this.avgs.push({ label, value: title.split('$')[1] })
        const data = []

        averages.forEach(point => {
          const { day, month, year } = point || {}
          if (!day || !month || !year) return

          const y = readDailyAverageValue(point, label)
          const date = new Date(year, month - 1, day)
          const x = date.getTime()
          data.push({ x, y })
        })

        data.sort((a, b) => a.x - b.x)
        series.push({ name: label, data })
      })

      this.series = series
      const preferred =
        series.find(item => item.name === 'TOTAL' || item.name === 'AVG_TOTAL') || series[0]
      if (!series.some(item => item.name === this.activeSeriesName)) {
        this.activeSeriesName = preferred ? preferred.name : ''
      }
      this.selectedDayTs = null
    },
    formatMoney(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '0'
      return n % 1 === 0
        ? String(n)
        : n.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
    },
  },
}
</script>

<style scoped>
.averages-card h1 {
  margin-bottom: 0;
}

.meta-panel {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 14px 16px;
}

.meta-title {
  color: #495057;
  font-weight: 600;
  font-size: 0.95rem;
}

.by-type-table th {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 600;
  border-bottom: 1px solid #dee2e6 !important;
}

.by-type-table td {
  font-size: 0.85rem;
  vertical-align: middle;
  padding-top: 0.35rem;
  padding-bottom: 0.35rem;
}

.avg-visual {
  margin-bottom: 1rem;
}

.avg-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 0.85rem;
}

.avg-kpi {
  background: linear-gradient(180deg, #eef5ff 0%, #ffffff 100%);
  border: 1px solid #90caf9;
  border-radius: 10px;
  padding: 0.75rem 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.avg-kpi span {
  color: #1565c0;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.avg-kpi strong {
  color: #0d47a1;
  font-size: 1.1rem;
  line-height: 1.2;
}

.avg-kpi small {
  color: #546e7a;
  font-size: 0.75rem;
  font-weight: 600;
}

.avg-measures {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.85rem;
}

.avg-measure {
  border: 1px solid #64b5f6;
  border-radius: 999px;
  background: #fff;
  color: #0d47a1;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 0.3rem 0.75rem;
  cursor: pointer;
}

.avg-measure.is-active {
  background: #1565c0;
  border-color: #0d47a1;
  color: #fff;
  box-shadow: 0 2px 6px rgba(21, 101, 192, 0.35);
}

.avg-section-title {
  margin-bottom: 0.15rem;
  color: #0d47a1;
  font-weight: 700;
}

.avg-hint {
  margin-bottom: 0.55rem;
  color: #546e7a;
  font-size: 0.8rem;
}

.avg-compare-chart {
  background: #f7fbff;
  border: 1px solid #bbdefb;
  border-radius: 10px;
  padding: 8px 6px 4px;
}

.avg-daily-chart {
  background: #f7fbff;
  border: 1px solid #bbdefb;
  border-radius: 10px;
  padding: 4px 4px 0;
}

.avg-compare-chart >>> .apexcharts-yaxis,
.avg-compare-chart >>> .apexcharts-datalabels,
.avg-daily-chart >>> .apexcharts-yaxis,
.avg-daily-chart >>> .apexcharts-datalabels {
  pointer-events: none;
}

.avg-compare-chart >>> .apexcharts-bar-area,
.avg-daily-chart >>> .apexcharts-bar-area {
  cursor: pointer;
}

.avg-selection {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.35rem;
  border: 0;
  border-radius: 999px;
  background: #1565c0;
  color: #fff;
  font-weight: 700;
  padding: 0.35rem 0.8rem;
  cursor: pointer;
}

.avg-selection span {
  color: #bbdefb;
  font-weight: 600;
}

.radial-chart-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 15px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  min-height: 280px;
  border: 2px solid transparent;
  cursor: pointer;
}

.radial-chart-container:hover,
.radial-chart-container.is-active {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.radial-chart-container.is-active {
  border-color: #1565c0;
}

.chart-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;
}

.chart-label {
  width: 100%;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  min-height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e9ecef;
}

.chart-label h6 {
  color: #495057;
  font-weight: 600;
  font-size: 0.85rem;
  line-height: 1.2;
  margin: 0;
  text-align: center;
}

@media (max-width: 991px) {
  .avg-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .radial-chart-container {
    padding: 5px;
  }
}
</style>
